import { classList } from '@findigs/helpers/src/helpers/class-list';
import { polymorphic } from '@findigs/helpers/src/helpers/polymorphic';
import type { PolymorphicHTMLProps } from '@findigs/helpers/src/helpers/polymorphic';
import { useAugmentQA } from '@findigs/helpers/src/hooks/use-augment-qa';
import { styles } from './avatar.module.scss';
import AnimalIcon from '../../images/animal-icon.inline.svg';

export const AVATAR_VARIANT = ['Person', 'Animal'] as const;

export type AvatarVariant = (typeof AVATAR_VARIANT)[number];

export type AvatarProps = BlockAriaAttributes<PolymorphicHTMLProps> & {
  firstName: string;
  lastName?: string;
  /**
   * The label to display right of the avatar.
   * @default undefined
   */
  sideLabel?: string;
  src?: string;
  variant: AvatarVariant;
  /** @example 'Guarantor' */
  badge?: string;
  /**
   * The maximum number of characters to display in the badge.
   *
   * @default 1
   */
  badgeCharLimit?: number;
  /** blocked since already handled internally */
  alt?: never;
  /** blocked since already handled internally */
  title?: never;
};

/**
 * Extends the vanilla `img` element with our styling.
 * All vanilla `img` props are forwarded.
 *
 * This is a component optimized for displaying avatars.
 *
 * This component is [polymorphic](/docs/component-system-polymorphic-components--docs).
 */
export const Avatar = polymorphic(
  { element: 'img', forwardRef: true },
  (
    {
      className,
      style,
      children,
      variant,
      badge,
      badgeCharLimit = 1,
      sideLabel,
      firstName,
      lastName,
      ...props
    }: AvatarProps,
    Component,
    ref
  ) => {
    const initials = (firstName[0] ?? '') + (lastName?.at(0) ?? '');
    const ariaLabel = `${firstName}${lastName ? ` ${lastName}` : ''}${
      badge ? ` (${badge})` : ''
    }`;

    const badgeText = badge?.slice(0, badgeCharLimit);
    const badgeMultiChar = (badge?.length ?? 0) > 1;

    const classes = classList('bubble', { badge, badgeMultiChar }, className);
    return (
      <div
        className={styles}
        {...useAugmentQA({ qa: `${variant.toLowerCase()}-avatar-wrapper` })}
      >
        <div
          style={style}
          className={classes}
          aria-label={ariaLabel}
          {...useAugmentQA({ qa: `${variant.toLowerCase()}-avatar` })}
        >
          <span aria-hidden={true}>
            {variant === 'Animal' ? (
              <AnimalIcon width="16" height="16" />
            ) : (
              initials
            )}
          </span>
          {props.src ? (
            <Component {...props} aria-hidden={true} ref={ref}>
              {children}
            </Component>
          ) : null}
          {badgeText && badgeText.length > 0 ? (
            <div className="badgeWrapper" aria-hidden={true}>
              <div className="badge">{badgeText}</div>
            </div>
          ) : null}
        </div>
        {sideLabel && <div className="sideLabel">{sideLabel}</div>}
      </div>
    );
  }
);
